import { AffiliateListResponse } from '@/@types/Affiliates';
import { api } from './api';
import { RequestedOnMarketplaceEnum } from '@/@types/Enums/marketplace';

export interface AffiliateProgramResponse {
  data: {
    id: string;
    contact_email: string;
    page_url: string | null;
    attribution: string;
    description?: string;
    cookie_duration: '30' | '60' | '90';
    commission: number;
    enable_affiliate_program: boolean;
    manual_affiliation_approval: boolean;
    access_buyer_contact_info: boolean;
    enable_marketplace: boolean;
    requested_on_marketplace: RequestedOnMarketplaceEnum;
    pre_cbk_responsible: string;
    commission_rule_type: string;
    commission_value: number;
    currency: string;
  };
}

interface ConfigureAffiliateProgramData {
  productId: string;
  contact_email: string;
  attribution: string;
  cookie_duration: number;
  commission: number;
  enable_affiliate_program: boolean;
  manual_affiliation_approval: boolean;
  access_buyer_contact_info: boolean;
  commission_rule_type?: string;
  commission_value?: number;
  currency?: string;
  description?: string;
  requested_on_marketplace?: RequestedOnMarketplaceEnum;
  pre_cbk_responsible?: string;
  page_url?: string;
}

export interface EditInviteAffiliateProgramData {
  productInvitationId: string;
  commission_value?: number;
  commission_rule_type?: string;
  currency?: string;
}

export interface InviteDetailsResponse {
  id: string;
  product: {
    id: string;
    name: string;
    producer_name: string;
    images: { url: string }[];
  };
  attribution: string;
  cookie_duration: number;
  commission_rule_type: string;
  commission_value: number;
  commission_value_formatted?: string;
  currency?: string;
}

interface ApplyAffiliateProgramData {
  productId: string;
  partner_invite_code?: string;
}

async function getAffiliateProgram(
  productId: string,
): Promise<AffiliateProgramResponse> {
  const response = await api.get(
    `/v1/products/${productId}/affiliate-programs`,
  );
  return response.data;
}

async function configureAffiliateProgram(
  data: ConfigureAffiliateProgramData,
): Promise<any> {
  const { productId, ...rest } = data;

  const payload: Record<string, any> = { ...rest };

  const ruleType = rest.commission_rule_type;
  if (rest.commission_value !== undefined) {
    const numeric = Number(rest.commission_value);
    payload.commission_value =
      ruleType === 'percentage' ? Math.round(numeric * 100) / 100 : numeric;
  }

  const normalizedPayload = {
    ...payload,
    contact_email: payload.contact_email?.trim().toLowerCase(),
  };
  return await api.post(
    `/v1/products/${productId}/affiliate-programs`,
    normalizedPayload,
  );
}

async function getAffiliatesById(
  productId: string,
  query: string,
  status: string,
  page: number,
): Promise<AffiliateListResponse> {
  const params = new URLSearchParams();

  params.append('page', page.toString());

  if (query) {
    params.append('q', query);
  }

  if (status !== 'all') {
    params.append('status', status);
  }

  const response = await api.get(
    `/v1/invitations/affiliates/${productId}?${params.toString()}`,
  );
  return response.data;
}

async function editInviteAffiliate(
  data: EditInviteAffiliateProgramData,
): Promise<any> {
  const {
    productInvitationId,
    commission_value,
    commission_rule_type,
    currency,
  } = data;

  const payload: any = {};

  if (commission_rule_type) payload.commission_rule_type = commission_rule_type;

  if (commission_value !== undefined) {
    const numeric = Number(commission_value);
    payload.commission_value =
      commission_rule_type === 'percentage'
        ? Math.round(numeric * 100) / 100
        : numeric;
  }

  // Para CPA o backend ainda depende de `currency`
  if (commission_rule_type === 'cpa' && currency) payload.currency = currency;

  return await api.post(
    `/v1/invitations/affiliates/${productInvitationId}/update`,
    payload,
  );
}

async function removeAffiliate(productInvitationId: string) {
  const response = await api.post(
    `/v1/invitations/affiliates/${productInvitationId}/remove`,
  );
  return response.data;
}

async function acceptInvite(productInvitationId: string) {
  const response = await api.post(
    `/v1/invitations/affiliates/${productInvitationId}/accepted`,
  );
  return response.data;
}

async function denyInvite(productInvitationId: string) {
  const response = await api.post(
    `/v1/invitations/affiliates/${productInvitationId}/deny`,
  );
  return response.data;
}

async function getInviteAffiliateProgramById(
  productId: string,
): Promise<InviteDetailsResponse> {
  const response = await api.get(
    `/v1/externals/${productId}/affiliate-program`,
  );
  return response.data.data;
}

async function applyAffiliateProgram(
  data: ApplyAffiliateProgramData,
): Promise<{ message: string }> {
  const { productId, partner_invite_code } = data;
  const body: Record<string, string> = {};
  if (partner_invite_code) {
    body.partner_invite_code = partner_invite_code;
  }
  return await api.post(`/v1/externals/${productId}/affiliate-program`, body);
}

export const AffiliateProgramService = {
  getAffiliateProgram,
  configureAffiliateProgram,
  getAffiliatesById,
  editInviteAffiliate,
  removeAffiliate,
  acceptInvite,
  denyInvite,
  getInviteAffiliateProgramById,
  applyAffiliateProgram,
};
